import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { RootStateOrAny } from 'react-redux';

import { makeAPIRequestReactQuery } from '../api';
import { endpoints } from '../endpoints';

import type { ClinicalNote } from './useExistingNotes';

interface VideaNotesBatchRequest {
  appointmentIds: number[];
}

interface VideaNotesBatchResponse {
  appointments: {
    [key: string]: ClinicalNote[];
  };
}

/**
 * Fetches clinical notes for multiple unified appointments in a single API request.
 *
 * Takes an array of appointment IDs and returns clinical notes organized by appointment ID.
 * Used to efficiently load notes data for multiple appointments without making individual
 * API calls for each appointment.
 *
 * @param unifiedAppointmentIds - Array of appointment IDs as strings
 * @param enabled - Whether the query should be enabled (default: true)
 * @returns Query result containing notes data organized by appointment ID
 */
export const useBatchNotesByUnifiedAppointmentIds = (
  unifiedAppointmentIds: string[],
  enabled: boolean = true,
) => {
  const {
    primaryPracticeId: currentPracticeId,
    primaryOrganizationId: currentOrganizationId,
  } = useSelector((state: RootStateOrAny) => state.login.user);

  // Convert string IDs to numbers
  const numericIds = useMemo(() => {
    return unifiedAppointmentIds
      .map((id) => parseInt(id))
      .filter((id) => !isNaN(id));
  }, [unifiedAppointmentIds]);

  // Create a single query for all appointment IDs
  const query = useMemo(
    () => ({
      queryKey: [
        endpoints.ambient.voiceNotes.batchNotesByUnifiedAppointmentIds,
        numericIds,
      ],

      queryFn: async () => {
        if (!numericIds.length) {
          return { appointments: {} };
        }

        const response =
          await makeAPIRequestReactQuery<VideaNotesBatchResponse>(
            endpoints.ambient.voiceNotes.batchNotesByUnifiedAppointmentIds,
            undefined,
            'POST',
            {
              headers: {
                practice: currentPracticeId,
                org: currentOrganizationId,
              },
              data: {
                appointmentIds: numericIds,
              } as VideaNotesBatchRequest,
            },
          );

        return response;
      },

      enabled:
        enabled &&
        !!numericIds.length &&
        !!currentPracticeId &&
        !!currentOrganizationId,
      staleTime: 10 * 1000, // 10 seconds
    }),
    [numericIds, currentPracticeId, currentOrganizationId, enabled],
  );

  return useQueries({ queries: [query] });
};
